import * as Sentry from "@sentry/react-native";
import * as Application from "expo-application";
import { useEffect, useState } from "react";

import SystemService from "../services/internal/system";

import { type Version } from "@/schemas/app/version";
import { isMobileApp } from "@/utils/platform";
import { getLatestAppVersion } from "@/utils/version";

const getCurrentVersion = (): Version => {
  const appVersion = Application.nativeApplicationVersion || "Unknown";
  if (appVersion === "Unknown") {
    const error = new Error("Unable to get current app version");
    throw error;
  } else {
    const [majorStr, minorStr, patchStr] = appVersion.split(".");
    const major = parseInt(majorStr ?? "0");
    const minor = parseInt(minorStr ?? "0");
    const patch = parseInt(patchStr ?? "0");
    return { major, minor, patch };
  }
};

const isNewerVersion = (current: Version, latest: Version): boolean => {
  if (latest.major > current.major) return true;
  if (latest.major === current.major && latest.minor > current.minor)
    return true;
  if (
    latest.major === current.major &&
    latest.minor === current.minor &&
    latest.patch > current.patch
  )
    return true;
  return false;
};

type CheckForAppUpdatesResult = {
  isChecking: boolean;
  isAvailable: boolean;
};

export async function isNewAppVersionAvailable(): Promise<boolean> {
  const systemService = new SystemService();
  try {
    const currentVersion = getCurrentVersion();
    const results = await systemService.getSupportedVersion();
    const latestVersion = getLatestAppVersion(results);
    return isNewerVersion(currentVersion, latestVersion);
  } catch (error) {
    Sentry.captureException(error);
  }
  return false;
}

export const useCheckForNewAppVersion = (): CheckForAppUpdatesResult => {
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    const checkForUpdates = async (): Promise<void> => {
      setIsChecking(true);
      try {
        const shouldUpdate = await isNewAppVersionAvailable();
        setIsAvailable(shouldUpdate);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsChecking(false);
      }
    };

    if (isMobileApp()) {
      checkForUpdates();
    }
  }, []);

  return { isAvailable, isChecking };
};
